/**
 * FILE-OVERVIEW:
 * - 役割: アプリのエントリーポイント。Amplify初期化とルート描画を担当。
 * - 主な連携: App.js, index.css, aws-amplify 設定
 * - 本番運用上の注意: 初期化順序を崩すと認証・API通信が失敗する。
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import { Authenticator, ThemeProvider, View, Image, Heading, Text } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import awsExports from './aws-exports';
import App from './App';
import { hexToRgb } from '@mui/material';
import '@aws-amplify/auth';
import '@aws-amplify/storage';
Amplify.configure(awsExports);

// 明示的に Auth / Storage 実装を Amplify に紐付ける（ホスティング環境でのバンドル差異対応）
(async function attachAmplifyPlugins() {
  try {
    // まず @aws-amplify/auth を試す（副作用 import だけで見つからない場合に備える）
    const authMod = await import('@aws-amplify/auth').catch(() => null);
    if (authMod) {
      // module の形状に合わせて正規化して Amplify.Auth に割り当てる
      const AuthImpl = authMod.Auth || authMod.default || authMod;
      if (AuthImpl) {
        Amplify.Auth = AuthImpl;
        console.log('Attached Amplify.Auth from @aws-amplify/auth');
      }
    } else {
      // フォールバックで aws-amplify の Auth を参照（存在すれば）
      const am = await import('aws-amplify').catch(() => null);
      if (am && am.Auth) {
        Amplify.Auth = am.Auth;
        console.log('Attached Amplify.Auth from aws-amplify');
      }
    }

    // storage も同様に紐付けておくと安全
    const storageMod = await import('@aws-amplify/storage').catch(() => null);
    if (storageMod) {
      const StorageImpl = storageMod.Storage || storageMod.default || storageMod;
      if (StorageImpl) {
        Amplify.Storage = StorageImpl;
        console.log('Attached Amplify.Storage from @aws-amplify/storage');
      }
    }
  } catch (e) {
    console.warn('attachAmplifyPlugins error', e);
  }
})();
 
(async function() {
  try {
    const mod = await import('aws-amplify/auth').catch(() => null);
    if (!mod) return;

    // Amplify v6 のAuth API可用性を起動時に軽く確認
    const hasAuthApi = typeof mod.fetchAuthSession === 'function' || typeof mod.getCurrentUser === 'function';
    console.log('has Auth API (v6)?', hasAuthApi);
    if (!hasAuthApi) return;

    try {
      const [session, user] = await Promise.all([
        typeof mod.fetchAuthSession === 'function' ? mod.fetchAuthSession().catch(() => null) : Promise.resolve(null),
        typeof mod.getCurrentUser === 'function' ? mod.getCurrentUser().catch(() => null) : Promise.resolve(null),
      ]);

      const idToken = session?.tokens?.idToken?.toString?.() || null;
      console.log('startup auth:', {
        username: user?.username || null,
        hasIdToken: !!idToken,
      });
    } catch (e) {
      console.error('startup auth check failed', e);
    }
  } catch (e) {
    console.error(e);
  }
})();

document.title = '取材スケジュール管理';

const theme = {
  name: 'memo-app-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          '10': '#e3f2fd',
          '80': '#1976d2',
          '100': '#1565c0',
        },
      },
    },
    components: {
      authenticator: {
        router: {
          borderWidth: '0',
          backgroundColor: '#ffffff',
          /*
          boxShadow: '0px 4px 8px 0px rgba(255, 255, 255, 0.12%)',
          */
          boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          minHeight: '100dvh',
          height: '100%',
        },
        form: {
          padding: '2rem',
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: 'none',
          margin: '0',
          minWidth: '320px',
          maxWidth: '360px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          height: '100%',
        },
      },
      button: {
        primary: {
          backgroundColor: '{colors.brand.primary.80}',
          color: '#ffffff',
          borderRadius: '8px',
          fontWeight: 'bold',
        },
      },
      fieldcontrol: {
        _focus: {
          borderColor: '{colors.brand.primary.80}',
        },
      },
    },
  },
};

// ログイン画面のヘッダーや説明をカスタム
const components = {
  Header() {
    return (
      <View textAlign="center" padding="2rem 0 1rem 0" margin="120px auto 30px auto ">
        {/*
        <Image
          alt="Memo App ロゴ"
          src="https://img.icons8.com/fluency/96/000000/notepad.png"
          width="64px"
          height="64px"
          style={{ marginBottom: '1rem' }}
        />
        */}
        <Heading level={3} color="#1976d2" style={{ fontSize: '1.4rem', fontWeight: 600 }}>
          取材スケジュール管理
        </Heading>
        {/*
        <Text color="#555" fontSize="1rem">
          ログインしてスケジュールを管理しましょう
        </Text>
        */}
      </View>
    );
  },
  /*
  Footer() {
    return (
      <View textAlign="center" padding="1rem 0 0 0">
        <Text color="#aaa" fontSize="0.9rem">
          &copy; 2025 Daily Tohoku Memo App
        </Text>
      </View>
    );
  },
  */
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <Authenticator components={components} hideSignUp={true}>
        {({ signOut, user }) => (
          <App user={user} signOut={signOut} />
        )}
      </Authenticator>
    </ThemeProvider>
  </React.StrictMode>
);